import React, { useState, useEffect, useCallback, useRef } from "react";
import SearchWithAPI from "../searchbox/searchbox.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getGalleryListApi, exportGalleryPdfApi } from "./gallery-api.js";
import "./gallery.css";
import "../common/skeleton-effect.css";
import vdownload from "../../assets/svg/vdownload.svg";
import closeIcon from "../../assets/svg/close.svg";
import calendarIcon from "../../assets/svg/calender_new.svg";
import timeIcon from "../../assets/svg/time.svg";
import searchIcon from "../../assets/svg/search.svg";
import { Row, Col, Spinner } from "react-bootstrap";
import { format } from "date-fns";
import GalleryView from "./gallery-view.js";
import { processExportResponse, triggerFileDownload } from "./gallery-export-helper.js";

const GalleryList = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiAdminUrl = process.env.REACT_APP_ADMIN_API_URL;

  const [galleryList, setGalleryList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showGalleryView, setShowGalleryView] = useState(false);
  const [selectedGalleryItem, setSelectedGalleryItem] = useState(null);
  const [refreshToggle, setRefreshToggle] = useState(false);
  const itemsPerPage = 15;

  // Filters
  const [searchText, setSearchText] = useState("");
  const [dockType, setDockType] = useState("");
  const [dockNumberOption, setDockNumberOption] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [vehicleNumber, setVehicleNumber] = useState(null);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const fromCreatedDate = startDate ? format(startDate, "yyyy-MM-dd") : "";
  const toCreatedDate = endDate ? format(endDate, "yyyy-MM-dd") : "";

  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    dock_type: "",
    dock_id: "",
    location_id: "",
    reference_number: "",
    department_id: "",
    vehicle_number: "",
    from_date: "",
    to_date: "",
    from_time: "",
    to_time: "",
  });

  const dockTypeOptions = [
    { label: "Select", value: "" },
    { label: "Loading", value: "load_in" },
    { label: "Unloading", value: "load_out" },
  ];

  const fetchGalleryList = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        ...appliedFilters,
        limit: itemsPerPage,
        skip: itemOffset * itemsPerPage,
      }).toString();

      const response = await getGalleryListApi(queryParams);
      if (response?.data?.success) {
        const total = response.data.total_rec === undefined ? response.data.count || 0 : response.data.total_rec;
        const newItems = response.data.references || response.data.data || [];
        setTotalRecords(total);
        setPageCount(response.data.total_pgs || Math.ceil(total / itemsPerPage) || 1);

        if (itemOffset === 0) {
          setGalleryList(newItems);
        } else {
          setGalleryList((prev) => [...prev, ...newItems]);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGalleryList();
  }, [itemOffset, refreshToggle, appliedFilters]);

  const handleApply = () => {
    setAppliedFilters({
      search: searchText,
      dock_type: dockType,
      dock_id: dockNumberOption?.value || "",
      location_id: selectedLocation?.value || "",
      reference_number: referenceNumber?.value || "",
      department_id: selectedDepartment?.value || "",
      vehicle_number: vehicleNumber?.value || "",
      from_date: fromCreatedDate,
      to_date: toCreatedDate,
      from_time: startTime,
      to_time: endTime,
    });
    setGalleryList([]);
    if (itemOffset === 0) {
      setRefreshToggle((prev) => !prev);
    } else {
      setItemOffset(0);
    }
  };

  const handleReset = () => {
    setSearchText("");
    setDockType("");
    setDockNumberOption(null);
    setSelectedLocation(null);
    setReferenceNumber(null);
    setSelectedDepartment(null);
    setVehicleNumber(null);
    setStartDate(null);
    setEndDate(null);
    setStartTime("");
    setEndTime("");

    setAppliedFilters({
      search: "",
      dock_type: "",
      dock_id: "",
      location_id: "",
      reference_number: "",
      department_id: "",
      vehicle_number: "",
      from_date: "",
      to_date: "",
      from_time: "",
      to_time: "",
    });

    setGalleryList([]);
    if (itemOffset === 0) {
      setRefreshToggle((prev) => !prev);
    } else {
      setItemOffset(0);
    }
  };

  const observer = useRef();
  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && itemOffset < pageCount - 1) {
          setItemOffset((prev) => prev + 1);
        }
      }, { rootMargin: "200px" });
      if (node) observer.current.observe(node);
    },
    [loading, itemOffset, pageCount]
  );

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const queryParams = new URLSearchParams({
        ...appliedFilters,
        export: true,
      }).toString();

      const response = await exportGalleryPdfApi(queryParams);
      
      const result = await processExportResponse(response);
      if (result) {
        triggerFileDownload(result.blob, result.fileName);
      }
    } catch (err) {
      console.error("Export process failed:", err);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="gallery-container content_container">
      {/* Filters Section */}
      <div className="gallery-filter-panel">
        <h4>Filter</h4>
        <Row className="mb-3">
          <Col>
            <span className="gallery-filter-label">Search</span>
            <div className="gallery-input-wrapper">
              <img src={searchIcon} className="gallery-input-icon" alt="search" />
              <input type="text" className="gallery-filter-input gallery-input-with-icon" placeholder="Search" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
              {searchText && <button className="gallery-input-clear" onClick={() => setSearchText("")}><img src={closeIcon} alt="clear" width="12" height="12" /></button>}
            </div>
          </Col>
          <Col>
            <span className="gallery-filter-label">Dock Type</span>
            <div className="gallery-input-wrapper">
              <select className="gallery-filter-input" value={dockType} onChange={(e) => setDockType(e.target.value)}>
                {dockTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {dockType && <button className="gallery-input-clear" onClick={() => setDockType("")}><img src={closeIcon} alt="clear" width="12" height="12" /></button>}
            </div>
          </Col>
          <Col>
            <span className="gallery-filter-label">Dock Number</span>
            <SearchWithAPI onDropdownChange={(opt) => setDockNumberOption(opt)} apiUrl={apiUrl + `dockmaster/autosuggestion`} params={{}} placeholder="Select" selectedOption={dockNumberOption} reset={true} />
          </Col>
          <Col>
            <span className="gallery-filter-label">Location</span>
            <SearchWithAPI onDropdownChange={(opt) => setSelectedLocation(opt)} apiUrl={apiAdminUrl + `get_location`} params={{}} placeholder="Select" selectedOption={selectedLocation} reset={true} />
          </Col>
          <Col>
            <span className="gallery-filter-label">Reference Number</span>
            <SearchWithAPI onDropdownChange={(opt) => setReferenceNumber(opt)} apiUrl={apiUrl + `get_refrence_number?term=`} params={{}} placeholder="Select" selectedOption={referenceNumber} reset={true} />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <span className="gallery-filter-label">Department</span>
            <SearchWithAPI onDropdownChange={(opt) => setSelectedDepartment(opt)} apiUrl={apiAdminUrl + `get_department`} params={{}} placeholder="Select" selectedOption={selectedDepartment} reset={true} />
          </Col>
          <Col>
            <span className="gallery-filter-label">Vehicle Number</span>
            <SearchWithAPI onDropdownChange={(opt) => setVehicleNumber(opt)} apiUrl={apiUrl + `get_vehicle_number?term=`} params={{}} placeholder="Select" selectedOption={vehicleNumber} reset={true} />
          </Col>
          <Col>
            <span className="gallery-filter-label">Date Range</span>
            <div className="gallery-range-container">
              <div className="gallery-range-input-wrapper">
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  placeholderText="From"
                  className="gallery-range-input"
                  dateFormat="yyyy-MM-dd"
                />
                <img src={calendarIcon} className="gallery-range-icon" alt="calendar" />
              </div>
              <span className="gallery-range-separator">-</span>
              <div className="gallery-range-input-wrapper">
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  placeholderText="To"
                  className="gallery-range-input"
                  dateFormat="yyyy-MM-dd"
                />
                <img src={calendarIcon} className="gallery-range-icon" alt="calendar" />
              </div>
              {(startDate || endDate) && (
                <button className="gallery-input-clear" onClick={() => { setStartDate(null); setEndDate(null); }}>
                  <img src={closeIcon} alt="clear" width="12" height="12" />
                </button>
              )}
            </div>
          </Col>
          <Col>
            <span className="gallery-filter-label">Time Range</span>
            <div className="gallery-range-container">
              <div className="gallery-range-input-wrapper">
                <DatePicker
                  selected={startTime ? new Date(`2000-01-01T${startTime}`) : null}
                  onChange={(date) => setStartTime(date ? format(date, "HH:mm") : "")}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="HH:mm"
                  placeholderText="hh:mm"
                  className="gallery-range-input"
                />
                <img src={timeIcon} className="gallery-range-icon" alt="time" style={{ filter: 'grayscale(1) brightness(0.2)' }} />
              </div>
              <span className="gallery-range-separator">-</span>
              <div className="gallery-range-input-wrapper">
                <DatePicker
                  selected={endTime ? new Date(`2000-01-01T${endTime}`) : null}
                  onChange={(date) => setEndTime(date ? format(date, "HH:mm") : "")}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="HH:mm"
                  placeholderText="hh:mm"
                  className="gallery-range-input"
                />
                <img src={timeIcon} className="gallery-range-icon" alt="time" style={{ filter: 'grayscale(1) brightness(0.2)' }} />
              </div>
              {(startTime || endTime) && (
                <button className="gallery-input-clear" onClick={() => { setStartTime(""); setEndTime(""); }}>
                  <img src={closeIcon} alt="clear" width="12" height="12" />
                </button>
              )}
            </div>
          </Col>
          <Col className="d-flex align-items-end justify-content-end gap-2">
            <button className="gallery-btn-reset w-100" onClick={handleReset}>
              Reset
            </button>
            <button className="gallery-btn-apply w-100" onClick={handleApply}>
              Apply
            </button>
          </Col>
        </Row>
      </div>

      {/* Header and Export */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 style={{ fontWeight: "700", color: "#172B4D", margin: 0 }}>Total Records : {totalRecords}</h5>
        <button className="gallery-export-btn" onClick={handleExport} disabled={exportLoading}>
          {exportLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <img src={vdownload} alt="export" width="20" height="20" />
          )}
          {' '}Export
        </button>
      </div>

      {/* Grid */}
      <div className="gallery-grid">
        {loading && itemOffset === 0 ? (
          [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((id) => <div className="gallery-card skeleton skeleton-card" key={`skeleton-initial-${id}`} style={{ border: "none" }}></div>)
        ) : (
          <>
            {galleryList.map((item) => (
              <button
                type="button"
                className="gallery-card"
                key={item.reference_id || item.id || item.reference_no}
                onClick={() => {
                  setSelectedGalleryItem(item);
                  setShowGalleryView(true);
                }}
              >
                {(item.dock_name || item.dock_number) && <div className="gallery-card-badge">{item.dock_name || item.dock_number}</div>}
                {(item.image_url || item.attachment_url || item.image) && (
                  <img
                    src={item.image_url || item.attachment_url || item.image}
                    alt="Reference"
                    className="gallery-card-img"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
                <div className="gallery-card-overlay">
                  <span className="gallery-card-vehicle">{item.vehicle_number || item.vehiclestype || "-"}</span>
                  <div className="gallery-card-details">
                    <span className="gallery-card-ref">Ref. : <span className="gallery-card-ref-number">{item.reference_no || item.reference_number || "-"}</span></span>
                    <span style={{ fontSize: "16px", color: "#E5E5E5" }}>•</span>
                    <span className={`gallery-type-pill ${item.activity_type === "loading" || item.dock_type === "load_in" ? "gallery-type-loading" : "gallery-type-unloading"}`}>{item.activity_type === "loading" || item.dock_type === "load_in" ? "Loading" : "Unloading"}</span>
                  </div>
                </div>
              </button>
            ))}
            {loading && itemOffset > 0 && [1, 2, 3, 4, 5].map((id) => (
              <div className="gallery-card skeleton skeleton-card" key={`skeleton-more-${id}`} style={{ border: "none" }}></div>
            ))}
            {!loading && galleryList.length === 0 && (
              <div className="col-12 text-center py-5" style={{ gridColumn: "1 / -1" }}>
                <h4 style={{ color: "#8790A1" }}>No records found.</h4>
              </div>
            )}
            {/* Sentinel element for infinite scroll */}
            <div ref={lastItemRef} style={{ height: "10px", gridColumn: "1 / -1" }} />
          </>
        )}
      </div>

      {/* View Sidebar */}
      <GalleryView show={showGalleryView} onHide={() => setShowGalleryView(false)} referenceId={selectedGalleryItem?.reference_id} />
    </div>
  );
};

export default GalleryList;
